import React from "react";
import { Link } from "react-router-dom";
import { useTable } from "@/lib/store";
import { Card, Kicker, Chip, Empty } from "@/components/kit";
import { WhatNeedsYou } from "@/components/Actions";
import { ROMAN, fmtDate } from "@/lib/util";
import { CalendarCheck, ArrowUpRight } from "lucide-react";

/* The whole job on one screen: the next date, then what is waiting on you. */
export default function Home() {
  const { me, year, meetings } = useTable();
  if (!year) return <Empty line="No year has been opened yet." />;

  const next = meetings.find((m) => m.status !== "Done");
  const after = next ? meetings.find((m) => m.num === next.num + 1) : null;
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  return (
    <div className="space-y-8 stagger" data-testid="home-page">
      <header>
        <div className="kicker mb-1.5">{greeting}</div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">{me.name.split(" ")[0]}</h1>
      </header>

      {/* ------------------------------------------------- the next date */}
      {next ? (
        <Card className="p-6 sm:p-8" data-testid="next-chapter-card">
          <Kicker>Next chapter</Kicker>
          <div className="flex items-start gap-5 mt-4">
            <span className="roman text-[3rem] sm:text-[4rem] leading-none" style={{ opacity: 0.5 }}>
              {ROMAN[next.num - 1]}
            </span>
            <div className="min-w-0 flex-1">
              {next.date ? (
                <>
                  <h2 className="serif text-3xl sm:text-4xl leading-tight" data-testid="next-chapter-date">
                    {fmtDate(next.date)}
                  </h2>
                  <p className="text-base mt-2" style={{ color: "var(--ink-mute)" }}>
                    {next.title || "Untitled"}
                    {next.time ? ` · ${next.time}` : ""}
                    {next.venue ? ` · ${next.venue}` : ""}
                  </p>
                  <div className="mt-5 flex gap-2 flex-wrap items-center">
                    <Chip color="var(--green)">Date locked</Chip>
                    <Link to={`/chapters/${next.id}`} className="btn-quiet !min-h-0 !py-2" data-testid="open-next-chapter">
                      Open the chapter <ArrowUpRight size={12} />
                    </Link>
                    <Link to="/date-finder" className="btn-quiet !min-h-0 !py-2" data-testid="change-next-date">
                      Change the date
                    </Link>
                  </div>
                </>
              ) : (
                <>
                  <h2 className="serif text-3xl sm:text-4xl leading-tight" data-testid="next-chapter-date">
                    No date yet
                  </h2>
                  <p className="text-base mt-2" style={{ color: "var(--rust)" }}>
                    {me.isAdmin
                      ? "Nothing can be planned until this is locked."
                      : "Tell the table which dates work for you."}
                  </p>
                  <Link to="/date-finder" className="btn-ink mt-5 inline-flex" data-testid="lock-the-date">
                    <CalendarCheck size={14} />
                    {me.isAdmin ? "Lock the date" : "Mark my dates"}
                  </Link>
                </>
              )}
            </div>
          </div>
          {after && (
            <p className="text-[13px] mt-6 pt-5" style={{ color: "var(--ink-mute)", borderTop: "1px solid var(--hair)" }}
              data-testid="chapter-after-next">
              Then Chapter {ROMAN[after.num - 1]} — {after.date ? fmtDate(after.date) : "no date yet"}
            </p>
          )}
        </Card>
      ) : (
        <Card className="p-6" data-testid="next-chapter-card">
          <Kicker>Next chapter</Kicker>
          <p className="serif text-2xl mt-3">All eight are done. The year is complete.</p>
        </Card>
      )}

      {/* ------------------------------------------- what is waiting on you */}
      <WhatNeedsYou limit={6} />

      {/* ---------------------------------------------- one quiet line */}
      {year.northStar && (
        <p className="text-[13px]" data-testid="north-star-line" style={{ color: "var(--ink-mute)" }}>
          <span className="kicker !text-[9px] mr-2">North Star</span>
          {year.northStar}
        </p>
      )}
    </div>
  );
}
